import React, { useEffect, useMemo, useState } from 'react';
import {
  ReferenceField,
  Datagrid,
  TextField,
  useUnselectAll,
  linkToRecord,
  DateField,
  FunctionField
} from 'react-admin';
import CircularProgress from '@material-ui/core/CircularProgress';
import StatusSelectField from '../../core/components/StatusSelectField';
import { fundInvestorStatus } from '../fundinvestors/FundInvestorLookups';
import EditButtonWithOnClick from '../../core/components/EditButtonWithOnClick';
import { withStyles } from '@material-ui/core/styles';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import TenantConfigurationApi from '../../core/apis/TenantConfigurationApi';
import TagsField from '../../core/components/TagsField';

const hideForNow = true; // as short term solution (it should be removed in the future)

const styles = () => ({
  table: {
    '& tbody>.MuiTableRow-root:hover': {
      background: 'unset',
      cursor: 'default'
    }
  },
  label: {
    color: '#424242',
    fontSize: '14px',
    display: 'inline-block',
    paddingBottom: '10px'
  },
  expandLabel: {
    color: '#757575',
    fontSize: '14px',
    display: 'inline-block',
    transform: 'translateY(-10px)'
  },
  progress: {
    display: 'flex',
    justifyContent: 'center'
  },
  editButton: {
    color: '#757575',
    minWidth: '24px',
    padding: '6px 0',
    '& span': {
      paddingRight: 0
    }
  },
  editButtonColumnStyle: {
    width: '24px'
  }
});

const FundInvestorDatagrid = props => {
  const {
    handleFundClick,
    handleInvestorFundEditButtonClick,
    classes,
    expand,
    permissions,
    fundInvTagsList,
    ...datagridProps
  } = props;

  const notifyServerError = useNotifyServerError();

  //unselecting all the checkboxes from the deleting actions in the ComponenWillUnmount listener
  const unSelectAll = useUnselectAll(datagridProps.resource);
  const YN_FREEZEINVESTORS = 'YN_freezeInvestors';
  const [freezeEnabled, setFreezeEnabled] = useState(false);

  const formatNumberWithSeparatorsAndPrecision = (value, precision) => {
    //decimal separator is ",", and thousand separator is "."
    if (value) {
      let number = value.toLocaleString('en-US');
      let parts = number.toString().split('.');
      let numberPart = parts[0];
      numberPart = numberPart.replaceAll(',', '.');
      let decimalPart = parts[1];
      if (!decimalPart) {
        if (precision === null || precision === 2) {
          decimalPart = '00';
        } else if (precision === 1) {
          decimalPart = '0';
        }
      }
      return numberPart + (decimalPart ? ',' + decimalPart : '');
    }
    return null;
  };

  useEffect(() => () => unSelectAll(), [unSelectAll]);
  const hasReadFundPermission = useMemo(() => {
    return permissions.includes('AUTH_READ_FUND_NA');
  }, [permissions]);

  useEffect(() => {
    TenantConfigurationApi.get(YN_FREEZEINVESTORS)
      .then(response => {
        setFreezeEnabled(response?.pageData[0]?.value === 'Y' ? true : false);
      })
      .catch(error => notifyServerError(error));
  }, [notifyServerError]);

  if (props.total > 0) {
    return (
      <>
        {expand && <span className={classes.label}>Related Funds</span>}
        <Datagrid {...datagridProps} className={classes.table}>
          <ReferenceField
            source="id"
            reference="fundinvestors"
            label="Fund Name"
            link={record =>
              hasReadFundPermission
                ? `${linkToRecord(`/funds`, record.fundId)}`
                : false
            }
            sortBy="fundName"
          >
            <TextField source="fundName" />
          </ReferenceField>
          <ReferenceField
            source="fundId"
            reference="funds"
            label="A.K.A."
            link={false}
            sortable={false}
          >
            <TextField label="A.K.A." source="aka" />
          </ReferenceField>
          <TextField source="fundId" label="Fund ID" sortable={false} />
          <ReferenceField
            source="fundId"
            reference="funds"
            label="Fund Contact Email"
            link={false}
            sortable={false}
          >
            <TextField label="Fund Contact Email" source="emailAddresses[0]" />
          </ReferenceField>
          <TextField
            source="fundCurrency"
            label="Fund Currency"
            sortable={false}
          />
          {freezeEnabled && (
            <DateField
              source="startDate"
              label="Investor Freeze Start Date"
              sortable={false}
              options={{
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                timeZone: 'UTC'
              }}
            />
          )}
          {freezeEnabled && (
            <DateField
              source="endDate"
              label="Investor Freeze End Date"
              sortable={false}
              options={{
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                timeZone: 'UTC'
              }}
            />
          )}
          <FunctionField
            label="Initial Commitment"
            sortable={false}
            render={record =>
              record
                ? formatNumberWithSeparatorsAndPrecision(
                    record['initialCommitment'],
                    record['commitmentPrecision']
                  )
                : null
            }
          />
          <FunctionField
            label="Total Commitment"
            sortable={false}
            render={record =>
              record
                ? formatNumberWithSeparatorsAndPrecision(
                    record['totalCommitment'],
                    record['commitmentPrecision']
                  )
                : null
            }
          />
          {fundInvTagsList?.length > 0 && (
            <TagsField
              label="Fund-Investor Tags"
              source="fundinvestortags"
              tagType="fundInvestorTags"
              sortable={false}
            />
          )}
          <StatusSelectField
            label="Relationship"
            source="status"
            choices={fundInvestorStatus}
          />
          {handleFundClick || hideForNow ? (
            undefined
          ) : (
            <EditButtonWithOnClick
              label=""
              className={classes.editButton}
              headerClassName={classes.editButtonColumnStyle}
              onClick={handleInvestorFundEditButtonClick}
            />
          )}
        </Datagrid>
      </>
    );
  } else if (props.loaded) {
    return (
      <span className={expand ? classes.expandLabel : classes.label}>
        There are no related Funds to be displayed
      </span>
    );
  } else {
    return (
      <div className={classes.progress}>
        <CircularProgress />
      </div>
    );
  }
};

export default withStyles(styles)(FundInvestorDatagrid);
